import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';

import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { Button, type ButtonProps } from '../Button';
import type { BottomSheetAction, BottomSheetFooterLayout } from './BottomSheet.types';
import {
  bottomSheetFooterTextClass,
  bottomSheetFooterVariants,
} from './BottomSheet.variants';

export interface BottomSheetFooterContentProps {
  primaryAction?: BottomSheetAction;
  secondaryAction?: BottomSheetAction;
  footerLayout?: BottomSheetFooterLayout;
  footerText?: ReactNode;
  footerComponent?: ReactNode;
  /**
   * Children of a composed `<BottomSheetActions>`. Wins over the action props
   * and over `footerComponent`, and gets the DS footer button sizing.
   */
  actions?: ReactNode;
  /** The sheet's root id — actions without their own get `<id>-primary` / `<id>-secondary`. */
  testID?: string;
}

/**
 * The footer's *contents* — the action buttons (or a caller's
 * `footerComponent`) plus the optional helper line. Extracted from
 * {@link BottomSheetSurface} because the footer is rendered in two different
 * places depending on platform:
 *
 * - **Web / static** — inside the card, in flow (or absolutely pinned when
 *   `footerOverlay`), by the Surface itself.
 * - **Native** — through gorhom's `footerComponent` slot, so it stays put
 *   while the body scrolls AND rides above the keyboard. gorhom positions
 *   that slot outside the Surface's tree, so the Surface can't own it.
 *
 * Both paths must produce identical markup, hence one component.
 */
export function BottomSheetFooterContent({
  primaryAction,
  secondaryAction,
  footerLayout = 'stacked',
  footerText,
  footerComponent,
  actions,
  testID,
}: BottomSheetFooterContentProps) {
  const hasButtons = primaryAction != null || secondaryAction != null;
  const inline = footerLayout === 'inline';
  const hasActions = actions != null && actions !== false;

  const body = hasActions ? (
    <View className={bottomSheetFooterVariants({ layout: footerLayout })}>
      {sizeFooterButtons(actions, inline)}
    </View>
  ) : (
    (footerComponent ??
    (hasButtons ? (
      <View className={bottomSheetFooterVariants({ layout: footerLayout })}>
        {/* `inline` puts the secondary on the LEFT and the primary on the right
            (Figma 5456:48685); `stacked` leads with the primary (5456:48682). */}
        {inline
          ? [
              renderAction(secondaryAction, 'secondary', true, testID),
              renderAction(primaryAction, 'primary', true, testID),
            ]
          : [
              renderAction(primaryAction, 'primary', false, testID),
              renderAction(secondaryAction, 'secondary', false, testID),
            ]}
      </View>
    ) : null))
  );

  const textNode =
    footerText == null || footerText === false ? null : isTextLike(footerText) ? (
      <Text className={bottomSheetFooterTextClass}>{footerText}</Text>
    ) : (
      footerText
    );

  if (!body && !textNode) return null;

  return (
    <View
      className={cn(
        'flex w-full flex-col gap-[8px]',
        // Figma right-aligns a stacked button group. An inline row, a custom
        // footer and a composed `<BottomSheetActions>` all own their own
        // alignment — `items-end` would shrink a composed footer's non-Button
        // children (a status line, a checkbox row) to their content width.
        !inline &&
          !footerComponent &&
          !hasActions &&
          hasButtons &&
          'items-end justify-center',
      )}>
      {body}
      {textNode}
    </View>
  );
}
BottomSheetFooterContent.displayName = 'BottomSheetFooterContent';

/** True when the footer would render nothing — lets callers skip the slot. */
export function hasBottomSheetFooter(props: BottomSheetFooterContentProps): boolean {
  return (
    props.primaryAction != null ||
    props.secondaryAction != null ||
    props.footerComponent != null ||
    (props.actions != null && props.actions !== false) ||
    (props.footerText != null && props.footerText !== false)
  );
}

/**
 * Give a composed `<BottomSheetActions>`'s Button children Figma's footer
 * sizing — `xxl`, full-width in a stacked column, `flex-1` in an inline row.
 * Explicit props on the child win, and non-Button children pass through
 * untouched (a footer can hold a checkbox row or a price line).
 *
 * Recurses through fragments. A conditional footer is naturally written as
 * `{ready ? <>…</> : <>…</>}`, and `Children.map` hands that back as ONE
 * fragment element — so without this the buttons inside kept their own size
 * and the stacked column's `items-end` shrank them to their label width.
 */
function sizeFooterButtons(actions: ReactNode, inline: boolean): ReactNode {
  return Children.map(actions, (child) => {
    if (!isValidElement(child)) return child;
    if (child.type === Fragment) {
      const fragment = child as ReactElement<{ children?: ReactNode }>;
      return sizeFooterButtons(fragment.props.children, inline);
    }
    if (child.type !== Button) return child;
    const element = child as ReactElement<ButtonProps>;
    return cloneElement(element, {
      size: element.props.size ?? 'xxl',
      fullWidth: element.props.fullWidth ?? !inline,
      className: cn(inline && 'min-w-0 flex-1', element.props.className),
    });
  });
}

function renderAction(
  action: BottomSheetAction | undefined,
  role: 'primary' | 'secondary',
  inline: boolean,
  sheetTestID: string | undefined,
) {
  if (!action) return null;
  return (
    <Button
      key={role}
      variant={action.variant ?? (role === 'primary' ? 'primary' : 'secondary')}
      size="xxl"
      fullWidth={!inline}
      leftIcon={action.icon}
      disabled={action.disabled}
      loading={action.loading}
      onPress={action.onPress}
      accessibilityLabel={action.accessibilityLabel}
      testID={action.testID ?? subTestID(sheetTestID, role)}
      className={inline ? 'min-w-0 flex-1' : undefined}>
      {action.label}
    </Button>
  );
}

function isTextLike(node: unknown): boolean {
  if (node == null || typeof node === 'boolean') return false;
  if (typeof node === 'string' || typeof node === 'number') return true;
  if (Array.isArray(node)) return node.every(isTextLike);
  return false;
}
